// frontend/src/modules/lancamentos/pages/Lancamentos.js
import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ptBR } from 'date-fns/locale';
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from 'react-icons/fa';
import { useMediaQuery } from 'react-responsive';

function Lancamentos({ usuario }) {
  const empresaId = usuario?.empresa_id || 1;
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [itensPorPagina] = useState(5);
  const [filtros, setFiltros] = useState({
    tipo: '',
    status: '',
    dataInicio: null,
    dataFim: null
  });
  const [mostrarForm, setMostrarForm] = useState(false);
  const [editando, setEditando] = useState(null);
  const [modalConfirmacao, setModalConfirmacao] = useState(null);
  const [formData, setFormData] = useState({
    descricao: '',
    valor: '',
    tipo: 'RECEITA',
    status: 'PENDENTE',
    data_lancamento: new Date(),
    data_vencimento: '',
    forma_pagamento: '',
    categoria: '',
    pessoa_nome: '',
    pessoa_documento: '',
    observacoes: ''
  });

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const token = localStorage.getItem('token');

  // ============================================
  // BUSCAR LANÇAMENTOS
  // ============================================
  const fetchLancamentos = async () => {
    setLoading(true);
    setError('');
    try {
      let url = `/api/lancamentos/empresa/${empresaId}`;
      
      const params = new URLSearchParams();
      if (filtros.tipo) params.append('tipo', filtros.tipo);
      if (filtros.status) params.append('status', filtros.status);
      if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio.toISOString().split('T')[0]);
      if (filtros.dataFim) params.append('dataFim', filtros.dataFim.toISOString().split('T')[0]);
      
      if (params.toString()) url += '?' + params.toString();

      console.log('📝 Buscando lançamentos:', url);

      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Dados recebidos:', data.length, 'lançamentos');
      
      setLancamentos(data);
      setTotalPaginas(Math.ceil(data.length / itensPorPagina));
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLancamentos();
  }, [filtros, empresaId]);

  // ============================================
  // CRIAR/ATUALIZAR LANÇAMENTO
  // ============================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const url = editando 
        ? `/api/lancamentos/${editando.id}`
        : '/api/lancamentos';
      
      const method = editando ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          empresa_id: empresaId,
          valor: parseFloat(formData.valor),
          data_lancamento: formData.data_lancamento.toISOString().split('T')[0]
        })
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao salvar');
      }

      setMostrarForm(false);
      setEditando(null);
      resetForm();
      fetchLancamentos();
      alert(editando ? '✅ Lançamento atualizado!' : '✅ Lançamento criado!');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // EXCLUIR LANÇAMENTO
  // ============================================
  const handleDelete = async () => {
    try {
      const response = await fetch(`/api/lancamentos/${modalConfirmacao}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Erro ao excluir');
      }

      setModalConfirmacao(null);
      fetchLancamentos();
      alert('✅ Lançamento excluído!');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (item) => {
    setEditando(item);
    setFormData({
      descricao: item.descricao,
      valor: item.valor,
      tipo: item.tipo,
      status: item.status,
      data_lancamento: new Date(item.data_lancamento),
      data_vencimento: item.data_vencimento || '',
      forma_pagamento: item.forma_pagamento || '',
      categoria: item.categoria || '',
      pessoa_nome: item.pessoa_nome || '',
      pessoa_documento: item.pessoa_documento || '',
      observacoes: item.observacoes || ''
    });
    setMostrarForm(true);
  };

  const resetForm = () => {
    setFormData({
      descricao: '',
      valor: '',
      tipo: 'RECEITA',
      status: 'PENDENTE',
      data_lancamento: new Date(),
      data_vencimento: '',
      forma_pagamento: '',
      categoria: '',
      pessoa_nome: '',
      pessoa_documento: '',
      observacoes: ''
    });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFiltroChange = (e) => {
    setFiltros({ ...filtros, [e.target.name]: e.target.value });
    setPagina(1);
  };

  const limparFiltros = () => {
    setFiltros({ tipo: '', status: '', dataInicio: null, dataFim: null });
    setPagina(1);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  // Paginação
  const indexInicio = (pagina - 1) * itensPorPagina;
  const indexFim = indexInicio + itensPorPagina;
  const lancamentosPagina = lancamentos.slice(indexInicio, indexFim);

  if (loading && lancamentos.length === 0) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando lançamentos...</div>;
  }

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', marginBottom: '20px', gap: '10px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>💰 Lançamentos Financeiros</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Gerencie todas as receitas e despesas
          </p>
        </div>
        <button
          onClick={() => {
            setMostrarForm(!mostrarForm);
            if (!mostrarForm) setEditando(null);
            resetForm();
          }}
          style={{
            padding: isMobile ? '8px 16px' : '10px 20px',
            backgroundColor: mostrarForm ? '#dc3545' : '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'center',
            fontSize: isMobile ? '13px' : '14px',
            width: isMobile ? '100%' : 'auto'
          }}
        >
          {mostrarForm ? <><FaTimes /> Cancelar</> : <><FaPlus /> Novo Lançamento</>}
        </button>
      </div>

      {/* Mensagens de erro */}
      {error && (
        <div style={{ backgroundColor: '#ffebee', padding: '12px 20px', borderRadius: '4px', color: '#c62828', marginBottom: '15px', border: '1px solid #ffcdd2', fontSize: isMobile ? '13px' : '14px' }}>
          ❌ {error}
          <button 
            onClick={fetchLancamentos}
            style={{ marginLeft: '15px', padding: '4px 12px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Tentar Novamente
          </button>
        </div>
      )}

      {/* Formulário */}
      {mostrarForm && (
        <div style={{ backgroundColor: '#f8f9fa', padding: isMobile ? '15px' : '20px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #dee2e6' }}>
          <h3 style={{ marginTop: 0, fontSize: isMobile ? '16px' : '18px' }}>{editando ? '✏️ Editar Lançamento' : '📝 Novo Lançamento'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '15px' }}>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Descrição:*</label>
                <input type="text" name="descricao" value={formData.descricao} onChange={handleChange} required style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Valor:*</label>
                <input type="number" name="valor" value={formData.valor} onChange={handleChange} required step="0.01" style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Tipo:*</label>
                <select name="tipo" value={formData.tipo} onChange={handleChange} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}>
                  <option value="RECEITA">💰 Receita</option>
                  <option value="DESPESA">💸 Despesa</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Status:*</label>
                <select name="status" value={formData.status} onChange={handleChange} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}>
                  <option value="PENDENTE">⏳ Pendente</option>
                  <option value="PAGO">✅ Pago</option>
                  <option value="VENCIDO">❌ Vencido</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Data:*</label>
                <DatePicker 
                  selected={formData.data_lancamento} 
                  onChange={(date) => setFormData({ ...formData, data_lancamento: date })} 
                  dateFormat="dd/MM/yyyy" 
                  locale={ptBR} 
                  style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} 
                />
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Data de Vencimento:</label>
                <input type="date" name="data_vencimento" value={formData.data_vencimento} onChange={handleChange} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Forma de Pagamento:</label>
                <select name="forma_pagamento" value={formData.forma_pagamento} onChange={handleChange} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }}>
                  <option value="">Selecione...</option>
                  <option value="DINHEIRO">💵 Dinheiro</option>
                  <option value="CARTAO_CREDITO">💳 Cartão Crédito</option>
                  <option value="CARTAO_DEBITO">💳 Cartão Débito</option>
                  <option value="PIX">📱 PIX</option>
                  <option value="BOLETO">📄 Boleto</option>
                  <option value="TED">🏦 TED</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Categoria:</label>
                <input type="text" name="categoria" value={formData.categoria} onChange={handleChange} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Cliente/Fornecedor:</label>
                <input type="text" name="pessoa_nome" value={formData.pessoa_nome} onChange={handleChange} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
              </div>
              <div>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Documento:</label>
                <input type="text" name="pessoa_documento" value={formData.pessoa_documento} onChange={handleChange} style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ fontSize: isMobile ? '13px' : '14px' }}>Observações:</label>
                <textarea name="observacoes" value={formData.observacoes} onChange={handleChange} rows="3" style={{ width: '100%', padding: '8px', marginTop: '5px', border: '1px solid #ddd', borderRadius: '4px', fontSize: isMobile ? '13px' : '14px' }} />
              </div>
            </div>
            <div style={{ marginTop: '15px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="submit" disabled={loading} style={{ padding: isMobile ? '8px 20px' : '10px 30px', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px', flex: isMobile ? '1' : '0' }}>
                {loading ? 'Salvando...' : <><FaSave /> Salvar</>}
              </button>
              <button type="button" onClick={() => { setMostrarForm(false); setEditando(null); resetForm(); }} style={{ padding: isMobile ? '8px 20px' : '10px 30px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px', flex: isMobile ? '1' : '0' }}>
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* FILTROS RÁPIDOS */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
        <button 
          onClick={() => setFiltros({...filtros, tipo: ''})} 
          style={{ padding: isMobile ? '6px 12px' : '8px 16px', backgroundColor: filtros.tipo === '' ? '#007bff' : '#e9ecef', color: filtros.tipo === '' ? 'white' : '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '11px' : '13px' }}
        >
          📊 Todos
        </button>
        <button 
          onClick={() => setFiltros({...filtros, tipo: 'RECEITA'})} 
          style={{ padding: isMobile ? '6px 12px' : '8px 16px', backgroundColor: filtros.tipo === 'RECEITA' ? '#28a745' : '#e9ecef', color: filtros.tipo === 'RECEITA' ? 'white' : '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '11px' : '13px' }}
        >
          💰 Receitas
        </button>
        <button 
          onClick={() => setFiltros({...filtros, tipo: 'DESPESA'})} 
          style={{ padding: isMobile ? '6px 12px' : '8px 16px', backgroundColor: filtros.tipo === 'DESPESA' ? '#dc3545' : '#e9ecef', color: filtros.tipo === 'DESPESA' ? 'white' : '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '11px' : '13px' }}
        >
          💸 Despesas
        </button>
        <button 
          onClick={() => setFiltros({...filtros, status: ''})} 
          style={{ padding: isMobile ? '6px 12px' : '8px 16px', backgroundColor: filtros.status === '' ? '#007bff' : '#e9ecef', color: filtros.status === '' ? 'white' : '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '11px' : '13px' }}
        >
          📋 Todos Status
        </button>
        <button 
          onClick={() => setFiltros({...filtros, status: 'PAGO'})} 
          style={{ padding: isMobile ? '6px 12px' : '8px 16px', backgroundColor: filtros.status === 'PAGO' ? '#28a745' : '#e9ecef', color: filtros.status === 'PAGO' ? 'white' : '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '11px' : '13px' }}
        >
          ✅ Pago
        </button>
        <button 
          onClick={() => setFiltros({...filtros, status: 'PENDENTE'})} 
          style={{ padding: isMobile ? '6px 12px' : '8px 16px', backgroundColor: filtros.status === 'PENDENTE' ? '#ffc107' : '#e9ecef', color: filtros.status === 'PENDENTE' ? 'white' : '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '11px' : '13px' }}
        >
          ⏳ Pendente
        </button>
        <button 
          onClick={limparFiltros} 
          style={{ padding: isMobile ? '6px 12px' : '8px 16px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '11px' : '13px' }}
        >
          🔄 Limpar
        </button>
        <span style={{ marginLeft: 'auto', fontSize: isMobile ? '12px' : '14px', color: '#6c757d' }}>
          Total: {lancamentos.length} lançamentos
        </span>
      </div>

      {/* Listagem */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '10px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        {lancamentos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: isMobile ? '30px' : '40px' }}>
            <div style={{ fontSize: isMobile ? '36px' : '48px', marginBottom: '10px' }}>📭</div>
            <p style={{ color: '#6c757d', fontSize: isMobile ? '13px' : '14px' }}>Nenhum lançamento encontrado.</p>
            <button 
              onClick={() => setMostrarForm(true)}
              style={{ marginTop: '10px', padding: '8px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px' }}
            >
              <FaPlus /> Criar primeiro lançamento
            </button>
          </div>
        ) : isMobile ? (
          // ============================================
          // MOBILE - CARDS
          // ============================================
          <div>
            {lancamentosPagina.map((item) => (
              <div
                key={item.id}
                style={{
                  backgroundColor: '#f8f9fa',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '10px',
                  borderLeft: `4px solid ${item.tipo === 'RECEITA' ? '#28a745' : '#dc3545'}`
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                  <strong style={{ fontSize: '14px' }}>{item.descricao}</strong>
                  <span style={{ fontWeight: 'bold', color: item.tipo === 'RECEITA' ? '#28a745' : '#dc3545', fontSize: '14px' }}>
                    {formatCurrency(item.valor)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6c757d', flexWrap: 'wrap', gap: '5px' }}>
                  <span>📅 {formatDate(item.data_lancamento)}</span>
                  <span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: item.tipo === 'RECEITA' ? '#d4edda' : '#f8d7da',
                      color: item.tipo === 'RECEITA' ? '#155724' : '#721c24',
                      fontSize: '10px',
                      marginRight: '4px'
                    }}>
                      {item.tipo === 'RECEITA' ? '💰' : '💸'}
                    </span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '10px',
                      backgroundColor: item.status === 'PAGO' ? '#d4edda' : item.status === 'PENDENTE' ? '#fff3cd' : '#f8d7da',
                      color: item.status === 'PAGO' ? '#155724' : item.status === 'PENDENTE' ? '#856404' : '#721c24',
                      fontSize: '10px'
                    }}>
                      {item.status}
                    </span>
                  </span>
                </div>
                <div style={{ marginTop: '8px', display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #dee2e6', paddingTop: '8px' }}>
                  <button onClick={() => handleEdit(item)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', fontSize: '14px' }} title="Editar">
                    <FaEdit />
                  </button>
                  <button onClick={() => setModalConfirmacao(item.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '14px' }} title="Excluir">
                    <FaTrash />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          // ============================================
          // DESKTOP - TABELA
          // ============================================
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Data</th>
                  <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Descrição</th>
                  <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>Valor</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Tipo</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Status</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Categoria</th>
                  <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {lancamentosPagina.map((item) => (
                  <tr key={item.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '10px' }}>{formatDate(item.data_lancamento)}</td>
                    <td style={{ padding: '10px' }}>{item.descricao}</td>
                    <td style={{ padding: '10px', textAlign: 'right', color: item.tipo === 'RECEITA' ? '#28a745' : '#dc3545', fontWeight: 'bold' }}>
                      {formatCurrency(item.valor)}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', backgroundColor: item.tipo === 'RECEITA' ? '#d4edda' : '#f8d7da', color: item.tipo === 'RECEITA' ? '#155724' : '#721c24' }}>
                        {item.tipo === 'RECEITA' ? '💰' : '💸'}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '12px', backgroundColor: item.status === 'PAGO' ? '#d4edda' : item.status === 'PENDENTE' ? '#fff3cd' : '#f8d7da', color: item.status === 'PAGO' ? '#155724' : item.status === 'PENDENTE' ? '#856404' : '#721c24' }}>
                        {item.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>{item.categoria || '-'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <button onClick={() => handleEdit(item)} style={{ background: 'none', border: 'none', color: '#007bff', cursor: 'pointer', marginRight: '10px' }} title="Editar">
                        <FaEdit />
                      </button>
                      <button onClick={() => setModalConfirmacao(item.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer' }} title="Excluir">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginação */}
            {totalPaginas > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
                <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1} style={{ padding: '8px 16px', border: '1px solid #dee2e6', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>
                  ◀ Anterior
                </button>
                <span style={{ padding: '8px 16px' }}>
                  Página {pagina} de {totalPaginas}
                </span>
                <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas} style={{ padding: '8px 16px', border: '1px solid #dee2e6', borderRadius: '4px', background: 'white', cursor: 'pointer' }}>
                  Próximo ▶
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de Confirmação */}
      {modalConfirmacao && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', padding: isMobile ? '20px' : '30px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ marginTop: 0, fontSize: isMobile ? '18px' : '22px' }}>⚠️ Confirmar Exclusão</h3>
            <p style={{ fontSize: isMobile ? '14px' : '16px' }}>Tem certeza que deseja excluir este lançamento?</p>
            <p style={{ color: '#dc3545', fontSize: isMobile ? '12px' : '14px' }}>Esta ação não pode ser desfeita.</p>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setModalConfirmacao(null)} style={{ padding: isMobile ? '8px 16px' : '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px' }}>
                <FaTimes /> Cancelar
              </button>
              <button onClick={handleDelete} style={{ padding: isMobile ? '8px 16px' : '10px 20px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: isMobile ? '13px' : '14px' }}>
                <FaTrash /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Lancamentos;